import { SettingsState, Store } from '../store/types';
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';

import useApi from '../api';

import localTranslations from '../translations.local';
import config from '../config';
import { Typography, TypographyProps } from '@mui/material';
import { useDidMountEffect } from '../utils/mount';
import { PublicLocaleObj } from '../api/types';

export type SupportedLocaleType = {
    locale: string;
    title: string;
    icon?: string;
};

type TranslationContextType = {
    i18n: string;
    loadTranslations: () => Promise<boolean>;
    getSupportedLocales: () => Array<SupportedLocaleType>;
    translate: (key: string, ...parameters: any[]) => string;
};

const TranslationContext = createContext<TranslationContextType>({
    i18n: 'en',
    loadTranslations: async () => false,
    getSupportedLocales: () => [],
    translate: (key: string, ...parameters) => '',
});

type TranslationValuesType = { [key: string]: string };
type TranslationValuesResponse = {
    type: 'small' | 'full';
    loadedLocale: string;
    data: TranslationValuesType;
};
const TranslationContextProvider = ({
    children,
    allowedToSave = true,
}: PropsWithChildren<{ allowedToSave: boolean }>) => {
    const { getLocales, getTranslations } = useApi();
    const { i18n, networkError, branding } = useSelector<Store, SettingsState>(
        state => state.settings,
    );

    const [locales, setLocales] = useState<Array<PublicLocaleObj>>();
    const [response, setResponse] = useState<TranslationValuesResponse>();
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState<string>();

    const refresh = () => {
        if (
            i18n &&
            !loading &&
            (i18n !== response?.loadedLocale || response?.type === 'small')
        ) {
            setLoading(true);
            getTranslations(i18n).then(response => {
                if (response) {
                    setResponse(response);
                }
                setLoading(false);
            });
        }
        return () => {};
    };

    useEffect(() => {
        getLocales().then(response => {
            if (response) {
                setLocales(response);
            }
        });
    }, []);

    useEffect(refresh, [i18n]);

    useDidMountEffect(() => {
        if (response && i18n && !loading) {
            setLoaded(`${i18n}-${response.type}`);
        }
    }, [response]);

    const translationValue = response?.data;

    const getSupportedLocales = () => {
        if (networkError || !branding?.locales) {
            return config.defaultSupportedLocales;
        }

        return (
            locales?.map(item => {
                return { locale: item.locale, title: item.title, icon: '' };
            }) ?? []
        );
    };
    const translate = (key: string, ...parameters: string[] | number[]) => {
        if (translationValue && translationValue[key]) {
            let val = translationValue[key];
            for (const parameter of parameters) {
                val = val.replace('%s', parameter + '');
            }
            return val;
        }
        const localTransLocale = i18n.substring(0, 2);
        if (
            localTranslations &&
            localTranslations[localTransLocale] &&
            localTranslations[localTransLocale][key]
        ) {
            let val = localTranslations[localTransLocale][key];
            for (const parameter of parameters) {
                val = val.replace('%s', parameter + '');
            }
            return val;
        }
        return `${key} ${parameters.join(' ')}`;
    };

    return useMemo(() => {
        return (
            <LocalizationProvider
                adapterLocale={i18n.substring(0, 2)}
                dateAdapter={AdapterDayjs}>
                <TranslationContext.Provider
                    value={{
                        i18n,
                        loadTranslations: async () => {
                            try {
                                refresh();
                                return true;
                            } catch (e) {
                                return false;
                            }
                        },
                        getSupportedLocales,
                        translate,
                    }}>
                    {children}
                </TranslationContext.Provider>
            </LocalizationProvider>
        );
    }, [loaded]);
};

type TranslateComponentProps = {
    tag: string;
    typoVariant?: TypographyProps['variant'];
    color?: TypographyProps['color'];
    params?: any[];
};

export function TranslateComponent({
    tag,
    typoVariant,
    color,
    params,
}: TranslateComponentProps) {
    const { translate } = useTranslation();

    return (
        <Typography variant={typoVariant} color={color}>
            {translate(tag, ...(params ?? []))}
        </Typography>
    );
}

export type LocalizedTextComponentProps = {
    value: Array<{ locale: string; content: string }>;
    typoVariant?: TypographyProps['variant'];
};

export function LocalizedTextComponent({
    value,
    typoVariant,
}: LocalizedTextComponentProps) {
    const { i18n } = useTranslation();

    const item = value?.find(item => item.locale === i18n.substring(0, 2));
    if (!item) {
        return '';
    }

    const tmpEl = document.createElement('div');
    tmpEl.innerHTML = item.content;

    return (
        <Typography
            className="cutTextEnd"
            title={tmpEl.innerText}
            variant={typoVariant}>
            {tmpEl.innerText}
        </Typography>
    );
}

export const useTranslation = () => {
    return useContext(TranslationContext);
};

export default TranslationContextProvider;
